import { Stack, Box, IconButton } from '@mui/material';
import { useMemo } from 'react';
import { useNotesContext } from '@/components/tour/notes/hooks/useNotesContext';
import { useBoardContext } from '../../hooks/useBoardContext';
import { FAIcon } from '@packages/ui-components';
import { AnimatedContentSwitch } from '@/components/common/AnimatedContentSwitch';
import { SidebarMode } from '../../config';
import { Note } from '../../../note';
import { EditMenu } from '../../../EditMenu';
import { Backlog } from './components/backlog';
import { useNotesStore } from '@/components/tour/notes/stores/notes.store';

export function Sidebar() {
  const { isBacklogOpen, sidebarMode, setSidebarMode } = useBoardContext();
  const { handleMoveToNote } = useNotesContext();

  const selectedNote = useNotesStore((state) => state.selectedNote);
  const { selectNote } = useNotesStore((state) => state.actions);

  const mainMessage = useMemo(() => {
    return selectedNote?.messages?.find((message) => message?.isMainMessage) || null;
  }, [selectedNote?.messages]);

  return (
    <Stack
      sx={{
        width: isBacklogOpen ? '356px' : '0px',
        flexShrink: 0,
        transition: 'width 0.3s ease',
        overflow: 'hidden',
        boxShadow: isBacklogOpen ? '0px 8px 11px -5px rgba(0, 0, 0, 0.20)' : '',
        borderLeft: isBacklogOpen ? '1px solid var(--Gray-300, #D0D5DD)' : '',
      }}
    >
      <AnimatedContentSwitch<SidebarMode>
        mode={sidebarMode}
        orderedModes={[SidebarMode.Backlog, SidebarMode.Note]}
        contentVariants={{
          [SidebarMode.Backlog]: <Backlog />,
          [SidebarMode.Note]: (
            <Stack
              sx={{
                width: '100%',
                height: '100%',
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  padding: '10px 18px',
                  borderBottom: '1px solid var(--Gray-300, #D0D5DD)',
                  height: '60px',
                }}
              >
                <IconButton
                  onClick={() => {
                    setSidebarMode(SidebarMode.Backlog);
                    selectNote(null);
                  }}
                  sx={{ gap: 1, width: '24px', height: '24px' }}
                >
                  <FAIcon
                    icon="faChevronLeft"
                    color="#101828"
                    size={18}
                  />
                </IconButton>

                <Stack direction="row" gap="15px" alignItems="center">
                  <IconButton
                    onClick={() => {
                      handleMoveToNote(selectedNote);
                    }}
                    id={`target-button-${selectedNote?.id}`}
                    sx={{ width: '24px', height: '24px' }}
                  >
                    <FAIcon
                      icon="faLocationCrosshairs"
                      size={18}
                      color="#101828"
                    />
                  </IconButton>
                  {mainMessage ? (
                    <EditMenu
                      message={mainMessage}
                      onDeleteSuccess={() => setSidebarMode(SidebarMode.Backlog)}
                    />
                  ) : null}
                </Stack>
              </Stack>
              <Box
                sx={{
                  width: '100%',
                  flex: 1,
                  overflow: 'auto',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  /* IE and Edge */
                  msOverflowStyle: 'none',
                  /* Firefox */
                  scrollbarWidth: 'none',
                }}
              >
                <Note />
              </Box>
            </Stack>
          ),
        }}
      />
    </Stack>
  );
}
